import type { StorageApi } from '@backstage/frontend-plugin-api';
import type { Step } from 'react-joyride';
import { STORAGE_BUCKET } from './progress';

/** An issue whose author is thanked in the « Nouveautés » modal. */
export interface ThankedIssue {
  number: number;
  title: string;
  url: string;
}

export interface Thanks {
  login: string;
  name: string | null;
  url: string;
  issues: ThankedIssue[];
}

/**
 * `/release-notes.json`, written at build time by scripts/fetch-release-notes.mjs from the latest
 * published GitHub release (docs/whats-new.md).
 */
export interface ReleaseNotes {
  version: string;
  name: string;
  publishedAt: string;
  url: string;
  /** A minor or major version (new features): the modal offers a tour and a launcher stays. */
  important: boolean;
  /** Markdown of the GitHub release. */
  body: string;
  thanks: Thanks[];
}

/** What the user has seen of the release notes (bucket `onboarding`, key `whatsNew`). */
export type WhatsNewState = {
  /** The modal of this version was closed: it no longer opens on its own. */
  seenVersion?: string;
  /** The tour of this version was finished or skipped: its launcher is gone. */
  tourVersion?: string;
};

export const WHATS_NEW_KEY = 'whatsNew';
/** How long the launcher of an important release stays in the bottom left corner. */
export const LAUNCHER_DAYS = 14;

export function isReleaseNotes(value: unknown): value is ReleaseNotes {
  const v = value as Partial<ReleaseNotes> | null;
  return Boolean(
    v &&
      typeof v.version === 'string' &&
      typeof v.body === 'string' &&
      Array.isArray(v.thanks),
  );
}

export async function updateWhatsNew(
  storageApi: StorageApi,
  patch: Partial<WhatsNewState>,
) {
  const bucket = storageApi.forBucket(STORAGE_BUCKET);
  const state = bucket.snapshot<WhatsNewState>(WHATS_NEW_KEY).value ?? {};
  await bucket.set<WhatsNewState>(WHATS_NEW_KEY, { ...state, ...patch });
}

/** The launcher shows for an important release whose tour is not done, for two weeks. */
export function showLauncher(
  notes: ReleaseNotes,
  state: WhatsNewState,
  now = Date.now(),
): boolean {
  if (!notes.important || state.tourVersion === notes.version) return false;
  const published = Date.parse(notes.publishedAt);
  return (
    Number.isNaN(published) || now - published < LAUNCHER_DAYS * 86_400_000
  );
}

/** `### Title` sections of the notes (`## ` when there are none), with their text. */
export function sections(body: string): { title: string; text: string }[] {
  const level = /^### /m.test(body) ? 3 : 2;
  const out: { title: string; text: string }[] = [];
  let current: { title: string; text: string } | undefined;
  for (const line of body.split('\n')) {
    const heading = /^(#{1,6}) (.*)/.exec(line);
    if (heading) {
      // Another heading (« ## Correctifs ») closes the section.
      current =
        heading[1].length === level
          ? { title: heading[2].trim(), text: '' }
          : undefined;
      if (current) out.push(current);
    } else if (current) {
      current.text += `${line}\n`;
    }
  }
  return out.map(s => ({ ...s, text: s.text.trim() }));
}

/** Plain text of a markdown section, for a tour card: no links syntax, bullets kept. */
export function plainText(markdown: string): string {
  return markdown
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[*_`]/g, '')
    .replace(/^\s*-\s+/gm, '• ')
    .replace(/\s*\((?:#\d+[^)]*)\)/g, '');
}

/** Display name of a thanked author: their GitHub name, else their login. */
export const thanksName = (t: Thanks) => t.name ?? t.login;

/** Tour of an important release: one centered card per section, then the thanks. */
export function whatsNewSteps(notes: ReleaseNotes): Step[] {
  const cards = sections(notes.body);
  const steps: Step[] = cards.map(s => ({
    target: 'body',
    placement: 'center',
    title: s.title.replace(/\s*\((?:#\d+[^)]*)\)/g, ''),
    content: (
      <div style={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
        {plainText(s.text)}
      </div>
    ),
  }));
  if (notes.thanks.length) {
    steps.push({
      target: 'body',
      placement: 'center',
      title: 'Merci',
      content: `Ces nouveautés viennent de vos idées : merci à ${notes.thanks
        .map(thanksName)
        .join(', ')}. Une idée ? Ouvre une issue sur le repo du portail.`,
    });
  }
  return steps;
}
